'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { Search, X } from 'lucide-react';
import type { TenantRow } from './types';

export function GlobalSearch({ tenants }: { tenants: TenantRow[] }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return tenants
      .filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.slug.toLowerCase().includes(q) ||
          t.domains.some((d) => d.host.toLowerCase().includes(q)),
      )
      .slice(0, 8);
  }, [query, tenants]);

  const primaryHost = (t: TenantRow) =>
    t.domains.find((d) => d.is_primary && d.is_active)?.host ??
    t.domains.find((d) => d.is_active)?.host ??
    `${t.slug}.okai.pt`;

  return (
    <div className="sa-global-search" ref={rootRef}>
      <Search className="w-4 h-4 text-ink-500 flex-shrink-0" />
      <input
        type="search"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Pesquisar tenant, slug, dominio..."
        className="sa-search-input"
      />
      {query && (
        <button type="button" onClick={() => { setQuery(''); setOpen(false); }} className="text-ink-500 hover:text-ink-900">
          <X className="w-4 h-4" />
        </button>
      )}
      {open && query && results.length > 0 && (
        <div className="sa-search-dropdown">
          {results.map((t) => (
            <Link
              key={t.id}
              href="/super-admin/tenants"
              onClick={() => setOpen(false)}
              className="sa-search-result"
            >
              <div className="min-w-0">
                <p className="font-medium text-ink-900 truncate">{t.name}</p>
                <p className="text-xs text-ink-500 font-mono">{t.slug} · {primaryHost(t)}</p>
              </div>
              <span className={`sa-status sa-status-${t.status === 'active' ? 'ok' : 'muted'}`}>{t.status}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
